import { LoadingScreen } from "components/LoadingScreen";
import { toastErro } from "components/Toast";
import { useEffect, useMemo, useState } from "react";
import {
  ICustoEmLote,
  ILancamentoCriarLote,
  IProcessoEmLote,
} from "../../interface/IProcessoEmLote";
import { IEdicaoLoteGerenciarPagamento } from "../../interface/IEdicaoLote";
import { useCriarLote } from "../../hooks/useCriarLote";
import { IFormCriarLote } from "../CriarLote";
import { FormCriarLote } from "../CriarLote/FormCriarLote";
import { ListaProcessosLote } from "../CriarLote/ListaProcessosLote";
import { ModalAdicionarCusto } from "../CriarLote/ModalAdicionarCusto";
import { ModalSelecionarProcessos } from "../CriarLote/ModalSelecionarProcessos";
import { ModalSucessoEditarLote } from "./ModalSucessoEditarLote";

interface IProps {
  idLote: number;
  onVoltar: () => void;
  onSalvar: (status?: string) => void;
}

export function EditarLote({ idLote, onVoltar, onSalvar }: IProps) {
  const {
    loading,
    setLoading,
    optionsBeneficiarios,
    carregarBeneficiarios,
    processosVincular,
    setProcessosVincular,
    selectedRowsVincular,
    setSelectedRowsVincular,
    clearRowsVincular,
    buscarProcessosVincular,
    validarProcessos,
    validarPlacasChassis,
    optionsDescricao,
    carregarDescricoes,
    buscarServicosAReceber,
    buscarLoteParaEdicao,
    atualizarLote,
    enviarBoleto,
    onClickProcesso,
  } = useCriarLote();

  const [carregado, setCarregado] = useState(false);
  const [temArquivoReciboInicial, setTemArquivoReciboInicial] = useState(false);
  const [processos, setProcessos] = useState<IProcessoEmLote[]>([]);
  const [selectedRowsTabela, setSelectedRowsTabela] = useState<IProcessoEmLote[]>([]);
  const [clearRowsTabela, setClearRowsTabela] = useState(false);
  const [formData, setFormData] = useState<IFormCriarLote>({
    idBeneficiario: -1,
    numeroNfRecibo: "",
    valorNfRecibo: "",
    arquivoRecibo: null,
    arquivosBoleto: [],
    observacao: "",
    pagamentoUrgente: false,
  });

  const [showModalSelecionar, setShowModalSelecionar] = useState(false);
  const [showModalAdicionarCusto, setShowModalAdicionarCusto] = useState(false);
  const [showModalAdicionarCustoTabela, setShowModalAdicionarCustoTabela] = useState(false);
  const [showModalSucesso, setShowModalSucesso] = useState(false);
  const [novoStatus, setNovoStatus] = useState<string | undefined>();

  useEffect(() => {
    carregarBeneficiarios();
  }, []);

  useEffect(() => {
    buscarLoteParaEdicao(idLote).then(({ hasErro, data }) => {
      if (hasErro || !data) {
        onVoltar();
        return;
      }
      const lote = data as IEdicaoLoteGerenciarPagamento;

      setFormData({
        idBeneficiario: lote.idBeneficiario ?? -1,
        numeroNfRecibo: lote.numeroNfRecibo ?? "",
        valorNfRecibo:
          lote.valorNfRecibo != null ? Number(lote.valorNfRecibo).toFixed(2) : "",
        arquivoRecibo: null,
        arquivosBoleto: [],
        observacao: lote.observacao ?? "",
        pagamentoUrgente: lote.pagamentoUrgente ?? false,
      });

      setProcessos(
        (lote.processos ?? []).map((p) => ({
          idProcesso: p.idProcesso,
          placa: p.placa ?? null,
          renavam: p.renavam ?? null,
          chassi: p.chassi ?? null,
          nomeEmpresa: p.nomeEmpresa ?? null,
          nomeDivisao: p.nomeDivisao ?? null,
          nomeCentroCusto: p.nomeCentroCusto ?? null,
          servico: p.servico ?? null,
          aReceber: p.aReceber ?? null,
          custos: (p.custos ?? []).map((c) => ({
            idDescricao: c.idDescricao,
            label: c.label,
            valor: c.valor != null ? Number(c.valor).toFixed(2) : "0.00",
            observacao: c.observacao ?? "",
          })),
        })),
      );

      setTemArquivoReciboInicial(!!lote.temArquivoNfRecibo);
      setCarregado(true);
    });
  }, [idLote]);

  useEffect(() => {
    if (formData.idBeneficiario >= 0) {
      carregarDescricoes(formData.idBeneficiario);
    }
  }, [formData.idBeneficiario]);

  const custosKey = useMemo(
    () =>
      processos
        .map(
          (p) =>
            `${p.idProcesso}:${[...new Set(p.custos.map((c) => c.idDescricao))]
              .sort((a, b) => a - b)
              .join(",")}`,
        )
        .join("|"),
    [processos],
  );

  useEffect(() => {
    const pares = processos.flatMap((p) =>
      [...new Set(p.custos.map((c) => c.idDescricao))].map((idDescricao) => ({
        idProcesso: p.idProcesso,
        idDescricao,
      })),
    );
    if (!pares.length) return;

    let ativo = true;
    buscarServicosAReceber(pares).then((linhas) => {
      if (!ativo) return;
      const mapa = new Map(linhas.map((l) => [l.idProcesso, l.aReceber]));
      setProcessos((prev) =>
        prev.map((p) => ({ ...p, aReceber: mapa.get(p.idProcesso) ?? null })),
      );
    });
    return () => {
      ativo = false;
    };
  }, [custosKey]);

  const onSubmitAdicionarCusto = (custo: {
    idDescricao: number;
    label: string;
    valor: string;
    observacao: string;
  }) => {
    const rowsParaAdicionar = showModalAdicionarCusto
      ? selectedRowsVincular
      : selectedRowsTabela;

    setProcessos((atual) => {
      const mapa = new Map(atual.map((p) => [p.idProcesso, p]));

      for (const row of rowsParaAdicionar) {
        const processoExistente = mapa.get(row.idProcesso);

        if (processoExistente) {
          const indice = processoExistente.custos.findIndex(
            (c) => c.idDescricao === custo.idDescricao,
          );
          const novosCustos =
            indice !== -1
              ? processoExistente.custos.map((c, i) =>
                  i === indice
                    ? { ...c, valor: custo.valor, observacao: custo.observacao }
                    : c,
                )
              : [...processoExistente.custos, custo];

          mapa.set(row.idProcesso, {
            ...processoExistente,
            custos: novosCustos,
          });
        } else {
          mapa.set(row.idProcesso, {
            idProcesso: row.idProcesso,
            placa: row.placa ?? null,
            renavam: row.renavam ?? null,
            chassi: row.chassi ?? null,
            nomeEmpresa: row.nomeEmpresa ?? null,
            nomeDivisao: row.nomeDivisao ?? null,
            nomeCentroCusto: row.nomeCentroCusto ?? null,
            servico: row.servico ?? null,
            custos: [custo],
            aReceber: row.aReceber ?? null,
          });
        }
      }

      return Array.from(mapa.values());
    });

    setShowModalAdicionarCusto(false);
    setShowModalAdicionarCustoTabela(false);
    setShowModalSelecionar(false);
    setSelectedRowsVincular([]);
    setProcessosVincular([]);
  };

  const onRemoverProcessos = () => {
    setProcessos((atual) =>
      atual.filter(
        (p) => !selectedRowsTabela.some((r) => r.idProcesso === p.idProcesso),
      ),
    );
    setClearRowsTabela(true);
    setTimeout(() => setClearRowsTabela(false), 100);
    setSelectedRowsTabela([]);
  };

  const onSalvarLote = async () => {
    if (!processos.length) {
      toastErro("Adicione pelo menos um processo ao lote");
      return;
    }

    const lancamentos: ILancamentoCriarLote[] = processos.flatMap((p) =>
      p.custos.map((c: ICustoEmLote) => ({
        idProcesso: p.idProcesso,
        idDescricao: c.idDescricao,
        valor: c.valor,
        observacao: c.observacao,
      })),
    );

    const { hasErro, data } = await atualizarLote(idLote, {
      idBeneficiario: formData.idBeneficiario,
      numeroNfRecibo: formData.numeroNfRecibo,
      valorNfRecibo: formData.valorNfRecibo,
      arquivoRecibo: formData.arquivoRecibo,
      observacao: formData.observacao,
      pagamentoUrgente: formData.pagamentoUrgente,
      lancamentos,
    });

    if (hasErro) {
      toastErro(data?.message ?? "Erro ao salvar as alterações do lote");
      return;
    }

    setNovoStatus(data?.status);

    if (formData.arquivosBoleto.length) {
      setLoading(true);
      for (const arquivo of formData.arquivosBoleto) {
        await enviarBoleto(idLote, arquivo);
      }
      setLoading(false);
    }

    setShowModalSucesso(true);
  };

  const totalCustos = processos.reduce(
    (acc, p) => acc + p.custos.reduce((s, c) => s + Number(c.valor || 0), 0),
    0,
  );

  const valorRecibo = Number(formData.valorNfRecibo?.replace(/[^0-9.]/g, "") || 0);

  const valorDivergente =
    valorRecibo > 0 &&
    Math.round(totalCustos * 100) !== Math.round(valorRecibo * 100);

  const avisoSemRecibo = !temArquivoReciboInicial && !formData.arquivoRecibo;

  return (
    <>
      <LoadingScreen mostrar={loading} zIndex={2000} />

      {carregado && (
        <>
          <FormCriarLote
            optionsBeneficiarios={optionsBeneficiarios}
            formData={formData}
            setFormData={setFormData}
            onCancelar={onVoltar}
            onSelecionarProcessos={() => setShowModalSelecionar(true)}
            onSalvarLote={onSalvarLote}
            disabledSalvar={!processos.length}
            valorDivergente={valorDivergente}
            titulo={`Editar lote ${idLote}`}
            textoCancelar="Voltar"
            textoSalvar="Salvar"
            avisoSemRecibo={avisoSemRecibo}
          />

          <ListaProcessosLote
            processos={processos}
            setProcessos={setProcessos}
            selectedRows={selectedRowsTabela}
            setSelectedRows={setSelectedRowsTabela}
            clearRows={clearRowsTabela}
            totalCustos={totalCustos}
            valorDivergente={valorDivergente}
            onRemoverProcessos={onRemoverProcessos}
            onAdicionarCusto={() => {
              if (!selectedRowsTabela.length) {
                toastErro("Selecione pelo menos um processo");
                return;
              }
              setShowModalAdicionarCustoTabela(true);
            }}
            onSelecionarProcessos={() => setShowModalSelecionar(true)}
            onClickProcesso={onClickProcesso}
          />
        </>
      )}

      <ModalSelecionarProcessos
        show={showModalSelecionar}
        handleClose={() => {
          setShowModalSelecionar(false);
          setProcessosVincular([]);
          setSelectedRowsVincular([]);
        }}
        processosVincular={processosVincular}
        selectedRows={selectedRowsVincular}
        setSelectedRows={setSelectedRowsVincular}
        clearRows={clearRowsVincular}
        onBuscar={buscarProcessosVincular}
        onAdicionarCusto={() => {
          if (!selectedRowsVincular.length) {
            toastErro("Selecione pelo menos um processo");
            return;
          }
          setShowModalAdicionarCusto(true);
        }}
        validarProcessos={validarProcessos}
        validarPlacasChassis={validarPlacasChassis}
        onClickProcesso={onClickProcesso}
      />

      <ModalAdicionarCusto
        show={showModalAdicionarCusto || showModalAdicionarCustoTabela}
        handleClose={() => {
          setShowModalAdicionarCusto(false);
          setShowModalAdicionarCustoTabela(false);
        }}
        optionsDescricao={optionsDescricao}
        idFornecedor={formData.idBeneficiario}
        carregarDescricoes={carregarDescricoes}
        onSubmit={onSubmitAdicionarCusto}
      />

      <ModalSucessoEditarLote
        show={showModalSucesso}
        idLote={idLote}
        onClose={() => onSalvar(novoStatus)}
      />
    </>
  );
}
